// src/components/Employee/EditEmployeeModal.jsx
import React, { useState, useEffect } from 'react';

const EditEmployeeModal = ({ isOpen, onClose, onSave, employee, isSaving, departments = [], designations = [] }) => {
  const [formData, setFormData] = useState({
    employeeCode: '',
    title: '',
    firstName: '',
    middleName: '',
    lastName: '',
    nic: '',
    passportNumber: '',
    drivingLicense: '',
    dateOfBirth: '',
    gender: '',
    maritalStatus: '',
    nationality: '',
    personalEmail: '',
    workEmail: '',
    contactNumber: '',
    emergencyContact: '',
    emergencyContactRelationship: '',
    address: '',
    city: '',
    country: '',
    dateJoined: '',
    dateConfirmed: '',
    employmentType: '',
    employmentStatus: '',
    departmentId: '',
    designationId: '',
    basicSalary: '',
    bankName: '',
    bankBranch: '',
    accountNumber: '',
    accountName: '',
    isActive: true,
  });

  useEffect(() => {
    if (employee && isOpen) {
      setFormData({
        employeeCode: employee.employeeCode || '',
        title: employee.title || '',
        firstName: employee.firstName || '',
        middleName: employee.middleName || '',
        lastName: employee.lastName || '',
        nic: employee.nic || '',
        passportNumber: employee.passportNumber || '',
        drivingLicense: employee.drivingLicense || '',
        dateOfBirth: employee.dateOfBirth?.substring(0, 10) || '',
        gender: employee.gender || '',
        maritalStatus: employee.maritalStatus || '',
        nationality: employee.nationality || '',
        personalEmail: employee.personalEmail || '',
        workEmail: employee.workEmail || '',
        contactNumber: employee.contactNumber || '',
        emergencyContact: employee.emergencyContact || '',
        emergencyContactRelationship: employee.emergencyContactRelationship || '',
        address: employee.address || '',
        city: employee.city || '',
        country: employee.country || '',
        dateJoined: employee.dateJoined?.substring(0, 10) || '',
        dateConfirmed: employee.dateConfirmed?.substring(0, 10) || '',
        employmentType: employee.employmentType || '',
        employmentStatus: employee.employmentStatus || 'ACTIVE',
        departmentId: employee.departmentId || employee.department?.id || '',
        designationId: employee.designationId || employee.designation?.id || '',
        basicSalary: employee.basicSalary ?? '',
        bankName: employee.bankName || '',
        bankBranch: employee.bankBranch || '',
        accountNumber: employee.accountNumber || '',
        accountName: employee.accountName || '',
        isActive: Boolean(employee.isActive),
      });
    }
  }, [employee, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.employeeCode || !formData.firstName || !formData.lastName || !formData.nic || 
        !formData.contactNumber || !formData.departmentId || !formData.designationId || !formData.basicSalary) {
      alert('Please fill all required fields');
      return;
    }

    const payload = {
      employeeCode: formData.employeeCode,
      title: formData.title,
      firstName: formData.firstName,
      middleName: formData.middleName,
      lastName: formData.lastName,
      fullName: `${formData.title} ${formData.firstName} ${formData.middleName || ''} ${formData.lastName}`.trim(),
      nic: formData.nic,
      passportNumber: formData.passportNumber,
      drivingLicense: formData.drivingLicense,
      dateOfBirth: formData.dateOfBirth,
      gender: formData.gender,
      maritalStatus: formData.maritalStatus,
      nationality: formData.nationality,
      personalEmail: formData.personalEmail,
      workEmail: formData.workEmail,
      contactNumber: formData.contactNumber,
      emergencyContact: formData.emergencyContact,
      emergencyContactRelationship: formData.emergencyContactRelationship,
      address: formData.address,
      city: formData.city,
      country: formData.country,
      dateJoined: formData.dateJoined,
      dateConfirmed: formData.dateConfirmed,
      employmentType: formData.employmentType,
      employmentStatus: formData.employmentStatus,
      departmentId: parseInt(formData.departmentId),
      designationId: parseInt(formData.designationId),
      basicSalary: parseFloat(formData.basicSalary),
      bankName: formData.bankName,
      bankBranch: formData.bankBranch,
      accountNumber: formData.accountNumber,
      accountName: formData.accountName,
      isActive: Boolean(formData.isActive),
    };

    onSave(payload);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '800px' }}>
        <header className="modal-header">
          <h3>Edit Employee: {employee?.fullName}</h3>
          <button className="close-modal-btn" onClick={onClose} disabled={isSaving}>
            ×
          </button>
        </header>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <form id="editEmployeeForm" onSubmit={handleSubmit}>
            <div className="form-grid-modal">
              <label>Employee Code *</label>
              <input name="employeeCode" className="custom-input" value={formData.employeeCode} onChange={handleChange} required disabled={isSaving} />

              <label>Title</label>
              <select name="title" className="custom-select" value={formData.title} onChange={handleChange} disabled={isSaving}>
                <option value="">Select Title</option>
                <option value="Mr.">Mr.</option>
                <option value="Mrs.">Mrs.</option>
                <option value="Ms.">Ms.</option>
                <option value="Dr.">Dr.</option>
              </select>

              <label>First Name *</label>
              <input name="firstName" className="custom-input" value={formData.firstName} onChange={handleChange} required disabled={isSaving} />

              <label>Middle Name</label>
              <input name="middleName" className="custom-input" value={formData.middleName} onChange={handleChange} disabled={isSaving} />

              <label>Last Name *</label>
              <input name="lastName" className="custom-input" value={formData.lastName} onChange={handleChange} required disabled={isSaving} />

              <label>NIC *</label>
              <input name="nic" className="custom-input" value={formData.nic} onChange={handleChange} required disabled={isSaving} />

              <label>Passport Number</label>
              <input name="passportNumber" className="custom-input" value={formData.passportNumber} onChange={handleChange} disabled={isSaving} />

              <label>Driving License</label>
              <input name="drivingLicense" className="custom-input" value={formData.drivingLicense} onChange={handleChange} disabled={isSaving} />

              <label>Date of Birth</label>
              <input name="dateOfBirth" type="date" className="custom-input" value={formData.dateOfBirth} onChange={handleChange} disabled={isSaving} />

              <label>Gender</label>
              <select name="gender" className="custom-select" value={formData.gender} onChange={handleChange} disabled={isSaving}>
                <option value="">Select Gender</option>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
                <option value="OTHER">Other</option>
              </select>

              <label>Marital Status</label>
              <select name="maritalStatus" className="custom-select" value={formData.maritalStatus} onChange={handleChange} disabled={isSaving}>
                <option value="">Select Status</option>
                <option value="SINGLE">Single</option>
                <option value="MARRIED">Married</option>
                <option value="DIVORCED">Divorced</option>
                <option value="WIDOWED">Widowed</option>
              </select>

              <label>Nationality</label>
              <input name="nationality" className="custom-input" value={formData.nationality} onChange={handleChange} disabled={isSaving} />

              <label>Contact Number *</label>
              <input name="contactNumber" className="custom-input" value={formData.contactNumber} onChange={handleChange} required disabled={isSaving} />

              <label>Personal Email</label>
              <input name="personalEmail" type="email" className="custom-input" value={formData.personalEmail} onChange={handleChange} disabled={isSaving} />

              <label>Work Email</label>
              <input name="workEmail" type="email" className="custom-input" value={formData.workEmail} onChange={handleChange} disabled={isSaving} />

              <label>Emergency Contact</label>
              <input name="emergencyContact" className="custom-input" value={formData.emergencyContact} onChange={handleChange} disabled={isSaving} />

              <label>Emergency Relationship</label>
              <input name="emergencyContactRelationship" className="custom-input" value={formData.emergencyContactRelationship} onChange={handleChange} disabled={isSaving} />

              <label>Address</label>
              <input name="address" className="custom-input" value={formData.address} onChange={handleChange} disabled={isSaving} />

              <label>City</label>
              <input name="city" className="custom-input" value={formData.city} onChange={handleChange} disabled={isSaving} />

              <label>Country</label>
              <input name="country" className="custom-input" value={formData.country} onChange={handleChange} disabled={isSaving} />

              <label>Date Joined</label>
              <input name="dateJoined" type="date" className="custom-input" value={formData.dateJoined} onChange={handleChange} disabled={isSaving} />

              <label>Date Confirmed</label>
              <input name="dateConfirmed" type="date" className="custom-input" value={formData.dateConfirmed} onChange={handleChange} disabled={isSaving} />

              <label>Employment Type</label>
              <select name="employmentType" className="custom-select" value={formData.employmentType} onChange={handleChange} disabled={isSaving}>
                <option value="">Select Type</option>
                <option value="PERMANENT">Permanent</option>
                <option value="CONTRACT">Contract</option>
                <option value="INTERN">Intern</option>
                <option value="TEMPORARY">Temporary</option>
              </select>

              <label>Employment Status</label>
              <select name="employmentStatus" className="custom-select" value={formData.employmentStatus} onChange={handleChange} disabled={isSaving}>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                <option value="TERMINATED">Terminated</option>
                <option value="RESIGNED">Resigned</option>
              </select>

              <label>Department *</label>
              <select name="departmentId" className="custom-select" value={formData.departmentId} onChange={handleChange} required disabled={isSaving}>
                <option value="">Select Department</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>

              <label>Designation *</label>
              <select name="designationId" className="custom-select" value={formData.designationId} onChange={handleChange} required disabled={isSaving}>
                <option value="">Select Designation</option>
                {designations.map((desig) => (
                  <option key={desig.id} value={desig.id}>
                    {desig.name}
                  </option>
                ))}
              </select>

              <label>Basic Salary *</label>
              <input name="basicSalary" type="number" step="0.01" className="custom-input" value={formData.basicSalary} onChange={handleChange} required disabled={isSaving} />

              <label>Bank Name</label>
              <input name="bankName" className="custom-input" value={formData.bankName} onChange={handleChange} disabled={isSaving} />

              <label>Bank Branch</label>
              <input name="bankBranch" className="custom-input" value={formData.bankBranch} onChange={handleChange} disabled={isSaving} />

              <label>Account Number</label>
              <input name="accountNumber" className="custom-input" value={formData.accountNumber} onChange={handleChange} disabled={isSaving} />

              <label>Account Name</label>
              <input name="accountName" className="custom-input" value={formData.accountName} onChange={handleChange} disabled={isSaving} />

              <label>Active</label>
              <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleChange} disabled={isSaving} />
            </div>
          </form>
        </div>
        <footer className="modal-footer">
          <button className="btn btn-cancel" onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
          <button className="btn btn-save" type="submit" form="editEmployeeForm" disabled={isSaving}>
            {isSaving ? 'Updating...' : 'Update Employee'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default EditEmployeeModal;
